

import { useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

export interface RoundStep {
  id: string;
  title: string;
  status: string;
  round: number;
  /** Total matches in this round. When provided (> 0), renders a pick-progress dash row. */
  gameCount?: number;
  /** Matches the user has picked a winner for in this round. */
  picksMade?: number;
}

interface RoundStepperProps {
  rounds: RoundStep[];
  currentRoundId?: string;
  eventId: string;
  onNavigate: (undercardId: string) => void;
  /** When false, renders inline (no sticky top bar / full-width chrome). Defaults to true. */
  sticky?: boolean;
}

const STATUS_DOT: Record<string, string> = {
  COMPLETED: 'bg-verdigris',
  SCORING: 'bg-orange-400',
  LOCKED: 'bg-gold',
  OPEN: 'bg-verdigris',
  UPCOMING: 'bg-surface-4',
};

export function RoundStepper({ rounds, currentRoundId, eventId, onNavigate, sticky = true }: RoundStepperProps) {
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [currentRoundId]);

  if (rounds.length === 0) return null;

  const track = (
    <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto scrollbar-hide py-2.5 sm:py-3">
      {rounds.map((round) => {
        const isActive = round.id === currentRoundId;
        const isCompleted = round.status === 'COMPLETED' || round.status === 'SCORING';
        const dotColor = STATUS_DOT[round.status] || STATUS_DOT.UPCOMING;
        const gameCount = round.gameCount ?? 0;
        const picksMade = Math.min(round.picksMade ?? 0, gameCount);

        return (
          <div key={round.id} className="flex items-center flex-shrink-0">
            <button
              ref={isActive ? activeRef : undefined}
              onClick={() => onNavigate(round.id)}
              className={cn(
                'flex flex-col gap-1 w-28 sm:w-32 px-3 sm:px-4 py-2 rounded-lg text-[11px] sm:text-xs font-medium transition-all',
                isActive
                  ? 'bg-gold/15 text-gold border border-gold/30'
                  : isCompleted
                    ? 'text-warm-gray border border-surface-3 hover:text-cream hover:bg-surface-2'
                    : 'text-warm-gray/60 border border-surface-3 hover:text-cream hover:bg-surface-2'
              )}
            >
              <span className="flex items-center gap-1.5 sm:gap-2 w-full min-w-0">
                <span className={cn('w-2 h-2 rounded-full flex-shrink-0', dotColor)} />
                <span className={cn(
                  'uppercase tracking-wider truncate min-w-0',
                  isActive ? 'font-bold' : 'font-medium'
                )}>
                  {round.title}
                </span>
              </span>
              {/* Per-match pick progress — gold dash = picked, cream dash = not yet,
                  with a picked/total count. Dashes flex to fill the remaining width. */}
              {gameCount > 0 && (
                <span className="flex items-center gap-1.5 w-full pl-3.5">
                  <span className="flex items-center gap-0.5 flex-1 min-w-0">
                    {Array.from({ length: gameCount }).map((_, i) => (
                      <span
                        key={i}
                        className={cn('h-0.5 flex-1 min-w-0 rounded-full', i < picksMade ? 'bg-gold' : 'bg-cream/40')}
                      />
                    ))}
                  </span>
                  <span className="flex-shrink-0 text-[10px] leading-none tabular-nums font-semibold opacity-80">
                    {picksMade}/{gameCount}
                  </span>
                </span>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );

  if (!sticky) return track;

  return (
    <div className="sticky top-0 z-40 bg-surface/95 backdrop-blur-sm border-b border-surface-3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {track}
      </div>
    </div>
  );
}
